import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import TokenExchange from './TokenExchange';
import PayPalFiatCheckout from './PayPalFiatCheckout';
import { PLATFORM_FEE_PERCENT, API_BASE_URL } from '@/config';
import blockchainService from '../services/blockchainService';
import { MarketplaceOrder, Transaction } from '../types';

interface DualPaymentModalProps {
  order?: MarketplaceOrder;
  transaction: Transaction;
  serviceName: string;
  onSuccess: () => void;
  onClose: () => void;
  onCancel: () => void;
  token: string | null;
}

interface CryptoDetails {
  fromCurrency: string;
  amount: string;
}

const DualPaymentModal: React.FC<DualPaymentModalProps> = ({
  order,
  transaction,
  serviceName,
  onSuccess,
  onClose,
  onCancel,
  token,
}) => {
  if (!transaction) {
    console.error("DualPaymentModal rendered without a transaction object.");
    return null;
  }

  const [paymentMethod, setPaymentMethod] = useState<'fiat' | 'crypto'>('fiat');
  const [transactionState, setTransactionState] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const amountToPay = order
    ? order.priceAtPurchase * order.quantity
    : transaction.amount;

  const platformFee = ((amountToPay * PLATFORM_FEE_PERCENT) / 100).toFixed(2);

  useEffect(() => {
    resetTransaction();
  }, [order?.id, transaction.id]);

  const handleTransactionError = (message: string) => {
    setErrorMessage(message);
    setTransactionState('error');
  };

  const resetTransaction = () => {
    setErrorMessage('');
    setTransactionState('idle');
  };

  const getAuthHeaders = () => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const handlePayPalSuccess = (captureResult: any) => {
    // As you correctly diagnosed, PayPalFiatCheckout already handles the capture.
    // This function's only job is to update the UI state to success.
    // The redundant and destructive API call has been removed.
    setTransactionState('success');
  };

  const handleCryptoTransactionStart = async (details: CryptoDetails) => {
    if (!order) {
      handleTransactionError('Crypto payments are not available for this type of transaction yet.');
      return;
    }

    setTransactionState('pending');
    const RATES: Record<string, number> = { eth: 2200, kala: 0.15, usd: 1, eur: 1.09, inr: 0.012, matic: 0.55 };
    try {
      if (!order.item.seller.ethAddress) {
          throw new Error("The seller has not provided a valid ETH address for crypto payments.");
      }
      const fromRate = RATES[details.fromCurrency.toLowerCase() as keyof typeof RATES] || 0;
      const amountInUsd = parseFloat(details.amount) * fromRate;
      const ethRate = RATES['eth'] || 2200;
      const amountInEth = amountInUsd / ethRate;
      if (amountInEth <= 0) throw new Error("Calculated crypto amount is zero or negative.");

      const receipt = await blockchainService.sendTransaction(order.item.seller.ethAddress, amountInEth.toString());
      if (receipt) {
        // This part might need adjustment if crypto flow also becomes redundant
        await handlePayPalSuccess({ success: true, transactionHash: receipt.transactionHash });
      } else {
        throw new Error('Crypto transaction failed to return a receipt.');
      }
    } catch (error) {
      console.error('Crypto transaction failed:', error);
      handleTransactionError(error instanceof Error ? error.message : 'An unknown crypto error occurred.');
    }
  };

  const renderContent = () => {
    switch (transactionState) {
      case 'pending':
        return (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="w-12 h-12 text-kala-secondary animate-spin" />
            <p className="mt-4 text-lg text-white font-semibold">Processing Transaction...</p>
            <p className="text-sm text-kala-400">Please wait...</p>
          </div>
        );
      case 'success':
        return (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <CheckCircle className="w-16 h-16 text-green-500" />
            <p className="mt-4 text-xl text-white font-bold">Payment Successful!</p>
            <p className="text-sm text-kala-300">Your purchase of {serviceName} is complete.</p>
            <button onClick={onSuccess} className="mt-6 bg-kala-secondary text-kala-900 font-bold py-2 px-6 rounded-lg">Done</button>
          </div>
        );
      case 'error':
        return (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500" />
            <p className="mt-4 text-xl text-white font-bold">Payment Failed</p>
            <p className="text-sm text-red-300 px-4">{errorMessage || 'Something went wrong. Please try again.'}</p>
            <button onClick={resetTransaction} className="mt-6 bg-red-500 text-white font-bold py-2 px-6 rounded-lg">Try Again</button>
          </div>
        );
      case 'idle':
      default:
        return (
          <div>
            <div className="p-6 border-b border-kala-700">
              <h3 className="text-2xl font-bold text-white">{serviceName}</h3>
              <p className="text-kala-400">Total Cost: <span className="text-kala-secondary font-bold">${amountToPay.toFixed(2)}</span></p>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="text-sm font-bold text-kala-300 mb-2 block">Choose Payment Method</label>
                <div className="flex rounded-lg border border-kala-600 overflow-hidden">
                  <button 
                    onClick={() => setPaymentMethod('fiat')} 
                    className={`w-1/2 py-3 text-sm font-bold transition-colors ${paymentMethod === 'fiat' ? 'bg-kala-secondary text-kala-900' : 'bg-kala-800 text-kala-300 hover:bg-kala-700'}`}>
                    Pay with Card/PayPal
                  </button>
                  <button 
                    onClick={() => setPaymentMethod('crypto')} 
                    disabled={!order} // Disable crypto if not a marketplace order
                    className={`w-1/2 py-3 text-sm font-bold transition-colors ${paymentMethod === 'crypto' ? 'bg-kala-secondary text-kala-900' : 'bg-kala-800 text-kala-300 hover:bg-kala-700'} disabled:bg-kala-800/50 disabled:cursor-not-allowed`}>
                    Pay with Crypto
                  </button>
                </div>
              </div>
              {paymentMethod === 'fiat' ? (
                <div className="space-y-3">
                  <div className="text-xs text-kala-400 bg-kala-900/40 border border-kala-700 rounded-lg p-3">
                    A platform fee of ${platformFee} ({PLATFORM_FEE_PERCENT}%) is included in the total.
                  </div>
                  {amountToPay > 0 ? (
                    <PayPalFiatCheckout
                      amount={amountToPay}
                      currency={transaction.currency}
                      description={`Purchase of "${serviceName}" on KalaKrut`}
                      userId={order?.buyerId || transaction.userId}
                      marketplaceItemId={order?.itemId}
                      quantity={order?.quantity}
                      onSuccess={handlePayPalSuccess}
                      onError={handleTransactionError}
                      token={token}
                    />
                  ) : (
                    <div className="text-center text-kala-400 p-4 bg-kala-900/40 rounded-lg">
                      <p>The service cost must be greater than zero to proceed with payment.</p>
                    </div>
                  )}
                </div>
              ) : (
                <TokenExchange 
                  onClose={onClose}
                  isDualMode={true} 
                  onStartTransaction={handleCryptoTransactionStart}
                />
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-kala-800 border border-kala-700 rounded-2xl w-full max-w-md shadow-xl relative" onClick={(e) => e.stopPropagation()}>
         <button onClick={onClose} className="absolute top-4 right-4 text-kala-500 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        {renderContent()}
      </div>
    </div>
  );
};

export default DualPaymentModal;
